import React, {useEffect, useState, useReducer} from 'react';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import PropTypes from 'prop-types';
import {useRouter} from 'next/router';
import {
  PlusIcon, ExclamationIcon, StarIcon, PencilIcon, TrashIcon, CollectionIcon, ShareIcon,
} from '@heroicons/react/solid';
import {Formik, Form} from 'formik';
import _ from 'lodash';
import cx from 'classnames';
import * as yup from 'yup';

import CourseCard from '../../components/CourseCard';
import Link from '../../components/Link';
import {BackIcon} from '../../components/svg';
import DocumentHeader from '../../components/DocumentHeader';
import Button from '../../components/ButtonNew';
import {Badge, DifficultyToBadge} from '../../components/Badge';
import QuestionCommentSection from '../../components/QuestionCommentSection';
import SignupModal from '../../components/SignupModal';
import {TextInput} from '../../components/Forms';
import EditorPanel from '../../components/code/EditorPanel';
import {Panel, PanelGroup, PanelResizer} from '../../components/Panel';
import {Modal} from '../../components/Modal';
import FacePile from '../../components/FacePile';
import FadeOut from '../../components/FadeOut';
import {ListAskedAt} from '../../components/ListAskedAt';
import {incrementQuestionViews} from '../QuestionListPage/AddQuestionModal';
import {TopCompanyQueries} from '../QuestionListPage/SuggestedQueries';
import InteractiveLesson from '../LessonPage/InteractiveLesson';
import {AskedAtInterviews} from './AskedAtInterview';
import AdminControls from '../QuestionListPage/AdminControls';
import VideoAnswer from './VideoAnswer';
import Placeholder from './Placeholder';
import InterviewDetails from './InterviewDetails';
import {
  getQuestion,
  getInterviewsForQuestion,
  getTags,
  saveQuestion,
  unsaveQuestion,
  deleteQuestion,
  getQuestionsRelatedToId,
  mergeQuestions,
} from '../../api/api';
import {PermissionType} from '../../../../lib/constants/permissions';
import {Feature} from '../../../../lib/constants/tags';
import {useAppContext, useAnalytics, useNotifications} from '../../hooks';
import {createURLWithParams} from '../../util';

const MAX_RELATED_QUESTIONS = 10;
const MAX_COURSES = 2;

export const votingReducer = (voteState, voteAction) => {
  switch (voteAction.type) {
    case 'set':
      return {voted: voteAction.voted, count: voteAction.count};
    case 'add':
      saveQuestion(voteAction.id);
      return {voted: true, count: voteState.count + 1};
    case 'remove':
      unsaveQuestion(voteAction.id);
      return {voted: false, count: voteState.count - 1};
    default:
      return voteState;
  }
};

const toMostCommonRole = (interviews) => _.head(_(interviews)
  .filter('role')
  .countBy('role')
  .toPairs()
  .maxBy(_.last));

const SaveButton = ({
  voteCount, voted, onVote, question,
}) => {
  const hasExpertAnswer = Boolean(question?.lesson || question?.data?.video);
  return (
    <OverlayTrigger
      trigger={(hasExpertAnswer || voted) ? [] : ['hover', 'focus']} // only show when question does not have an expert answer
      placement="bottom"
      overlay={(
        <Tooltip>
          Save to get notified when we publish a new answer for this question.
        </Tooltip>
      )}
    >
      <button
        type="button"
        className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
        onClick={onVote}
      >
        <StarIcon className={cx('h-4 w-4 mr-1', {'text-gray-300': !voted, 'text-yellow-500': voted})} />
        {voted ? 'Saved' : 'Save'}
        <span className="border-l ml-1.5 pl-1.5 text-gray-400">{voteCount}</span>
      </button>
    </OverlayTrigger>
  );
};

const ShareButton = ({onShare}) => (
  <button
    type="button"
    className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
    onClick={onShare}
  >
    <ShareIcon className="h-4 w-4 mr-1 text-gray-300" />
    Share
  </button>
);

export const QuestionPage = () => {
  const [question, setCurrentQuestion] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const [tags, setTags] = useState({
    role: [], company: [], type: [],
  });
  const [signupModal, setSignupModal] = useState(false);
  const [mostCommonRole, setMostCommonRole] = useState(null);
  const {
    currentUser, referrer, hasPermission, features,
  } = useAppContext();
  const canEdit = hasPermission(PermissionType.QUESTION_EDIT);
  const canDelete = hasPermission(PermissionType.QUESTION_ARCHIVE);
  const {tracker} = useAnalytics();
  const {setSuccess} = useNotifications();
  const [vote, dispatchVote] = useReducer(votingReducer, {count: 0, voted: false});
  const [error, setError] = useState(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const router = useRouter();
  const {slug, id} = router.query;

  const onMerge = async ({targetId}) => {
    setError(null);
    try {
      const {data: targetQuestion} = await mergeQuestions(question.id, targetId);
      router.push(`/questions/${targetQuestion.id}/${targetQuestion.slug}`);
      setMergeModalOpen(false);
    } catch (err) {
      setError(err);
    }
  };

  const onDelete = async () => {
    await deleteQuestion(question.id);
    router.push('/questions');
  };

  useEffect(() => {
    const fetchData = async (questionResult) => {
      try {
        const [interviewsResult, relatedQns] = await Promise.all([
          getInterviewsForQuestion(id),
          getQuestionsRelatedToId(id),
        ]);

        setInterviews(interviewsResult);
        setRelatedQuestions(relatedQns);

        const mostCommonRoleSlug = toMostCommonRole(interviewsResult);

        const tagResult = await getTags({
          feature: Feature.Questions,
          role: mostCommonRoleSlug,
        });

        const mostCommonRoleTag = tagResult?.role?.find((tag) => tag.id === mostCommonRoleSlug);
        setMostCommonRole(mostCommonRoleTag);
        setTags(tagResult);

        dispatchVote({
          type: 'set',
          count: questionResult.votes,
          voted: questionResult.voted,
        });

        incrementQuestionViews();
        tracker.questionView(
          {id, slug: questionResult.slug, hasAI: Boolean(questionResult.duetAiInterviewType)},
        );
      } catch {
        router.push('/404');
      }
    };

    const handleQuestionFetch = async () => {
      try {
        let questionResult = question;

        if (!questionResult) { // if url has been corrected, don't refetch question data
          questionResult = await getQuestion(id, referrer);
          setCurrentQuestion(questionResult);
        }

        if (!slug) {
          router.replace(`/questions/${questionResult.id}/${questionResult.slug}`);
          return; // exit to avoid fetching data until URL is correct
        }

        fetchData(questionResult);
      } catch {
        router.push('/404');
      }
    };

    handleQuestionFetch();
  }, [id, slug]);

  const RelatedQuestions = ({className}) => (
    <div className={className}>
      <div className="flex flex-col">
        <h3 className="text-xl font-semibold mb-4">Related Questions</h3>
        {relatedQuestions?.slice(0, MAX_RELATED_QUESTIONS).map((q) => (
          <Link
            href={`/questions/${q.id}/${q.slug}`}
            className="block cursor-pointer p-4 mb-4 border border-gray-200 rounded-lg text-sm md:text-base font-semibold leading-none hover:border-gray-300 transition duration-50"
            onClick={() => tracker.questionClickRelated({
              related_id: q.id,
              related_slug: q.slug,
              id: question.id,
              question: question.id,
              slug: question.slug,
            })}
          >
            <div className="flex flex-col w-full">
              {q.companies && (
                <FadeOut className="-mr-2" right>
                  <ListAskedAt tags={q.companies} className="mb-0.5" />
                </FadeOut>
              )}
              <div className="flex">
                <div>{_.truncate(q.data.title, {length: 200})}</div>
                {q.commenters && <FacePile faces={q.commenters} small className="ml-auto" />}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );

  const isGated = !currentUser;
  const onTriggerSignup = () => setSignupModal((p) => !p);
  const pageTitle = question ? question.data.title : undefined;
  const pageDescription = question
    ? `Learn to answer common interview questions like '${question.data.title}' in the Exponent community.`
    : undefined;

  const onVote = () => {
    if (!isGated) {
      if (!vote.voted) {
        tracker.questionUpvote();
        setSuccess('Question saved!', (
          <span>
            You can find this question later in your
            {' '}
            <Link className="font-semibold text-indigo" href="/questions?filterBy=saved">Saved Questions</Link>
            .
          </span>
        ));
      }
      dispatchVote({type: !vote.voted ? 'add' : 'remove', id: question.id});
    } else {
      onTriggerSignup();
    }
  };

  const onShare = () => {
    tracker.questionShare({id: question?.id, slug: question?.slug});
    const baseURL = window.location.origin;

    if (question?.id && question?.slug) {
      const url = createURLWithParams(`${baseURL}/questions/${question.id}/${question.slug}`, {
        utm_source: 'user_share',
        utm_user: currentUser?.uuid || undefined,
      });

      navigator.clipboard.writeText(url)
        .then(() => {
          setSuccess('URL copied to clipboard!');
        })
        .catch((err) => {
          setError('Failed to copy: ', err);
        });
    } else {
      setError('Invalid question data. Unable to create URL.');
    }
  };

  const onEditCode = ({language, ...rest}) => {
    tracker.codeEditorEditCode({
      type: 'question', lesson: question.lesson.slug, language, difficulty: question.lesson.difficulty, ...rest,
    });
  };

  const onRunCode = ({language, ...rest}) => {
    tracker.codeEditorRunCode({
      type: 'question', lesson: question.lesson.slug, language, difficulty: question.lesson.difficulty, ...rest,
    });
  };

  const onRunTests = ({language, ...rest}) => {
    tracker.codeEditorRunTests({
      type: 'question', lesson: question.lesson.slug, language, difficulty: question.lesson.difficulty, ...rest,
    });
  };

  const onPassTests = ({language, ...rest}) => {
    tracker.codeEditorPassedTests({
      type: 'question', lesson: question.lesson.slug, language, difficulty: question.lesson.difficulty, ...rest,
    });
  };

  const showEditorPanel = question?.lesson?.codeProblem;
  const hasInteractiveLesson = question?.lesson?.lessonType === 'question';
  const showVideo = question?.data.video;

  return (
    <>
      <DocumentHeader
        title={pageTitle}
        metaDescription={pageDescription}
        socialDescription={pageDescription}
      />
      <div className="flex bg-white h-screen -mt-16 pt-16">
        <PanelGroup
          direction="horizontal"
          key={showEditorPanel} // TODO: Hack to resize Panels for coding questions
        >
          {/* Main content */}
          <Panel
            defaultSize={showEditorPanel ? 60 : 70}
            minSize={showEditorPanel ? 40 : 70}
            maxSize={showEditorPanel ? 90 : 80}
          >
            <div className="w-full h-screen overflow-auto">
              <AdminControls question={question} />
              <div className="md:w-4/5 max-w-3xl mx-auto">
                <QuestionContent
                  onVote={onVote}
                  voteCount={vote.count}
                  voted={vote.voted}
                  interviews={interviews}
                  question={question}
                />

                {showVideo && !hasInteractiveLesson && <VideoAnswer question={question} />}

                {question?.lesson && (
                  <div className="px-3 md:px-0">
                    {hasInteractiveLesson && (
                      <InteractiveLesson
                        lesson={question?.lesson}
                        hideIntroWithoutAccess={false}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* User action buttons */}
              <div className="px-3 md:px-0 md:w-4/5 max-w-3xl mx-auto flex gap-x-2 mt-4 overflow-auto scrollbar-hidden md:overflow-hidden">
                <SaveButton
                  voteCount={vote.count}
                  voted={vote.voted}
                  onVote={onVote}
                  question={question}
                />

                <Link
                  href={`/questions/contribute?basedOn=${question?.id}`}
                  className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => tracker.questionClickSeenQuestion(
                    {id: question?.id, question: question?.id},
                  )}
                >
                  <PlusIcon className="h-4 w-4 mr-1 text-gray-300" />
                  I was asked this
                </Link>

                <ShareButton onShare={onShare} />

                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`mailto:course-feedback@tryexponent.com?subject=Flagging /questions/${question?.id}/${question?.slug}`}
                  className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
                  onClick={() => tracker.questionClickFlag(
                    {id: question?.id, question: question?.id},
                  )}
                >
                  <ExclamationIcon className="h-4 w-4 mr-1 text-gray-300" />
                  Flag
                </a>

                {canEdit && (
                  <>
                    <button
                      type="button"
                      className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => router.push(`/questions/edit/${question?.id}`)}
                    >
                      <PencilIcon className="h-4 w-4 mr-1 text-gray-300" />
                      Edit
                    </button>
                    <button
                      type="button"
                      className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
                      onClick={() => setMergeModalOpen(true)}
                    >
                      <CollectionIcon className="h-4 w-4 mr-1 text-gray-300" />
                      Merge
                    </button>
                  </>
                )}
                {canDelete && (
                  <button
                    type="button"
                    className="flex items-center justify-center border px-2 py-1 text-sm font-semibold text-gray-500 rounded shadow-sm hover:bg-gray-100 whitespace-nowrap"
                    onClick={onDelete}
                  >
                    <TrashIcon className="h-4 w-4 mr-1 text-gray-300" />
                    Delete
                  </button>
                )}
              </div>

              {/* Interview details for mobile view or interactive lesson */}
              {!_.isEmpty(question?.category) && !_.isEmpty(interviews) && (
                <div className={`block lg:${showEditorPanel ? 'block' : 'hidden'}`}>
                  <div className="border-t border-b mt-6 bg-gray-50">
                    <InterviewDetails
                      question={question}
                      interviews={interviews}
                      tags={tags}
                      tracker={tracker}
                      className="md:w-4/5 max-w-3xl mx-auto px-3 md:px-0"
                    />
                  </div>
                </div>
              )}

              {features?.duet_ai_socrates === 'on' && question?.duetAiInterviewType && (
                <div className="md:w-4/5 max-w-3xl mx-auto px-3 md:px-0">
                  <div className="rounded-lg bg-indigo-100 mt-10 border border-indigo-200">
                    <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between">
                      <div className="flex flex-col">
                        <div className="text-lg font-semibold">Practice this question with AI</div>
                        <div className="text-base">
                          Try our new mock interview experience to get feedback instantly.
                        </div>
                      </div>
                      <div className="mt-4 md:mt-0 md:ml-4 w-full md:w-auto">
                        <Button
                          href={`/practice/ai/new?type=${question.duetAiInterviewType}&question=${question.slug}&v=transcription&src=questions`}
                          onClick={() => tracker.questionClickAiCta(
                            {type: question.duetAiInterviewType},
                          )}
                          target="_blank"
                          className="px-8 py-3 w-full md:w-auto bg-gradient-to-r from-indigo-600 to-purple-500 text-nowrap"
                        >
                          Practice with AI
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="md:w-4/5 max-w-3xl mx-auto">
                {question?.lesson && (
                <h3 className="mt-8 text-xl font-semibold px-3 md:px-0">
                  Community Answers
                </h3>
                )}

                <div className="mt-4 mb-20 px-3 md:px-0">
                  <div className="flex flex-col rounded-lg md:shadow-sm bg-gray-50 border border-grey-200 text-grey-900 p-4 mb-8">
                    <div className="font-semibold mb-2">💬 Community guidelines</div>
                    <ul className="ml-6 list-disc text-gray-600">
                      <li>
                        <span className="font-semibold">Stay on topic.</span>
                        {' '}
                        Use this section for submitting solutions and providing feedback to others.
                      </li>
                      <li>
                        <span className="font-semibold">Be inclusive.</span>
                        {' '}
                        Exponent is a diverse community.
                        Please respect others&apos; opinions and beliefs.
                      </li>
                    </ul>
                  </div>

                  <QuestionCommentSection
                    question={question}
                    onTriggerSignup={onTriggerSignup}
                    isGated={isGated}
                  />

                  <div className={`block lg:${showEditorPanel ? 'block' : 'hidden'}`}>
                    {!_.isEmpty(relatedQuestions) && <RelatedQuestions />}
                  </div>

                  {question?.courses?.length > 0 && (
                    <div className={`block lg:${showEditorPanel ? 'block' : 'hidden'} mb-8`}>
                      <h3 className="text-xl font-semibold mb-4">
                        Related Courses
                      </h3>
                      <div className="grid grid-cols-1 gap-2 md:grid-cols-2 md:gap-4">
                        {question.courses.slice(0, MAX_COURSES).map((course) => (
                          <CourseCard
                            course={course}
                            trackerEvent={() => tracker.questionClickRelatedCourse({
                              question: question.slug,
                              course: course.slug,
                            })}
                            urlParams={{src: 'question'}}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <TopCompanyQueries
                    role={mostCommonRole}
                    tags={tags}
                    className="mx-6 mb-8 md:m-0"
                  />
                </div>
              </div>
            </div>
          </Panel>

          <PanelResizer className="hidden lg:flex" />

          {/* Sidebar or Code Editor */}
          <Panel
            defaultSize={showEditorPanel ? 40 : 30}
            minSize={showEditorPanel ? 10 : 20}
            maxSize={showEditorPanel ? 60 : 30}
            className="hidden lg:flex"
          >
            {showEditorPanel ? (
              <EditorPanel
                lesson={question.lesson}
                onEditCode={onEditCode}
                onRunCode={onRunCode}
                onRunTests={onRunTests}
                onPassTests={onPassTests}
              />
            ) : (
              // Sidebar view of interview details and related questions
              <div className="w-full flex flex-col h-screen overflow-auto">
                <InterviewDetails
                  question={question}
                  interviews={interviews}
                  tags={tags}
                  tracker={tracker}
                  className="w-full px-4 mb-4"
                />

                {question?.courses?.length > 0 && (
                  <div className="px-4 flex flex-col gap-y-3 mb-8">
                    <h3 className="text-xl font-semibold mb-1">
                      Related Courses
                    </h3>
                    {question.courses.slice(0, MAX_COURSES).map((course) => (
                      <CourseCard
                        course={course}
                        trackerEvent={() => tracker.questionClickRelatedCourse({
                          question: question.slug,
                          course: course.slug,
                        })}
                        urlParams={{src: 'question'}}
                        showThumbnail={false}
                        showDescription={false}
                      />
                    ))}
                  </div>
                )}

                {!_.isEmpty(relatedQuestions) && <RelatedQuestions className="px-4 mb-20" />}
              </div>
            )}
          </Panel>
        </PanelGroup>
      </div>

      {/* Merge question modal */}
      <Modal
        isOpen={mergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        className="p-5"
        showCloseButton
        shouldCloseOnOverlayClick
        shouldCloseOnEsc
      >
        <Formik
          initialValues={{targetId: '', confirmWord: ''}}
          validationSchema={yup.object(
            {
              targetId: yup.number().required('Required'),
              confirmWord: yup.string().oneOf(['merge'], 'Type "merge" to submit the form').required('Type "merge" to submit the form'),
            },
          )}
          onSubmit={onMerge}
        >
          {({isSubmitting, values}) => (
            <Form className="flex flex-col gap-y-2 justify-between">

              <div className="text-lg font-bold">Merge this question</div>

              <div className="text-base text-red-600">
                All interviews and comments from this question will be transferred
                to the base question. This question will be deleted.
              </div>

              <TextInput
                label="Base Question ID:"
                name="targetId"
                placeholder="843"
                required
              />

              <TextInput
                label='Type "merge" to confirm your action'
                name="confirmWord"
                placeholder="merge"
                required
              />

              <Button
                type="submit"
                className="w-full"
                loading={isSubmitting}
                disabled={isSubmitting || !(values.targetId && values.confirmWord === 'merge')}
              >
                Submit
              </Button>

              {error && (
                <div className="mt-2 text-base font-medium leading-tight text-red-600">
                  {error.message}
                </div>
              )}
            </Form>
          )}
        </Formik>
      </Modal>
      <SignupModal isOpen={signupModal} source="socrates" />
    </>
  );
};

const QuestionContent = ({
  question,
  backLink,
  voteCount,
  voted,
  onVote,
  interviews,
}) => (
  <div className="px-3 md:px-0">
    <Link href="/questions" className="flex items-center font-medium text-gray-400 my-3">
      <BackIcon className="mr-3" />
      {backLink || <Link href="/questions">All Questions</Link>}
    </Link>
    {question ? (
      <div className="relative">
        <span className="text-xl md:text-2xl text-gray-900 font-medium leading-tight">
          <div className="flex justify-start items-center">
            <h1 className="inline align-middle mr-2">
              {question.data.title}
            </h1>
            {question?.lesson?.difficulty && (
              <Badge className="mr-2" color={DifficultyToBadge[question?.lesson?.difficulty]?.color}>
                {DifficultyToBadge[question?.lesson?.difficulty]?.text}
              </Badge>
            )}
            <div className="hidden md:block ml-auto">
              <SaveButton voteCount={voteCount} voted={voted} onVote={onVote} question={question} />
            </div>
          </div>
        </span>
        {question.data.content && (
        <div className="text-base text-gray-500 my-6">{question.data.content}</div>
        )}
        <div className="flex justify-start mt-2">
          <AskedAtInterviews question={question} interviews={interviews} />
        </div>
      </div>
    ) : <Placeholder animated />}
  </div>
);

QuestionContent.propTypes = {
  question: PropTypes.shape({
    created_at: PropTypes.string,
    data: PropTypes.any,
    id: PropTypes.number,
    slug: PropTypes.string,
    comment_count: PropTypes.number,
    voters: PropTypes.arrayOf(PropTypes.number),
  }),
  voted: PropTypes.bool.isRequired,
  voteCount: PropTypes.number.isRequired,
  backLink: PropTypes.string,
  onVote: PropTypes.func.isRequired,
};

QuestionContent.defaultProps = {
  question: null,
  backLink: null,
};

export default QuestionPage;
